import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { ProofGridView } from "./proof/ProofGridView";
import { supabase } from "../../lib/supabase";

// =============================================================================
// PATTERN SAMPLE MODAL
// =============================================================================
// PURPOSE: Shows sample conversations where a message pattern was used,
// displaying them in the 3-column ProofGridView with the pattern message
// highlighted using an amber ring. Helps admins determine if a pattern
// is ManyChat automation or a human-written script.
//
// HIGHLIGHTS: The pattern message (first setter message matching the pattern)
// is highlighted with an amber ring so users can see the message "in action"
// within the full conversation context.
// =============================================================================

interface PatternSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  patternId: string | null;
  patternText: string;
  occurrenceCount: number;
  organizationId: string;
  timezone: string;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null
  ) => void;
}

interface PatternConversation {
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  sent_at: string;
  setter_name: string;
}

export const PatternSampleModal: React.FC<PatternSampleModalProps> = ({
  isOpen,
  onClose,
  patternId,
  patternText,
  occurrenceCount,
  organizationId,
  timezone,
  onConversationClick,
}) => {
  const [data, setData] = useState<PatternConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  // Fetch pattern samples when modal opens
  const fetchSamples = useCallback(async () => {
    if (!patternId || !supabase) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data: samples, error: queryError } = await supabase.rpc(
        "get_pattern_sample_messages",
        { p_pattern_id: patternId, p_limit: 12 }
      );

      if (queryError) throw queryError;
      setData(samples || []);
    } catch (err) {
      console.error("Failed to fetch pattern samples:", err);
      setError(err instanceof Error ? err.message : "Failed to load samples");
    } finally {
      setIsLoading(false);
    }
  }, [patternId]);

  useEffect(() => {
    if (isOpen && patternId) {
      fetchSamples();
    } else {
      setData([]);
    }
  }, [isOpen, patternId, fetchSamples]);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  // Transform API data to ProofGridView format
  // Each sample includes highlightTimestamps pointing to the pattern message
  const conversations = data.map((conv) => ({
    conversation_id: conv.conversation_id,
    contact_name: conv.contact_name,
    lead_instagram: conv.lead_instagram,
    bucket: "pattern", // Used for color coding (will use gray)
    time_label: conv.setter_name || "Unknown",
    highlightTimestamps: [conv.sent_at], // Highlight the pattern message
  }));

  const truncatedText = patternText.length > 100 ? patternText.slice(0, 100) + "..." : patternText;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-8 pb-8"
      data-testid="pattern-sample-modal"
    >
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal — anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-6xl mx-4 flex min-h-0`}
      >
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.12] shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm bg-amber-500" />
                <h2 className="token-18 text-glass">Pattern Sample Usage</h2>
                <span className="token-14 text-glass-muted">
                  ({data.length} of {occurrenceCount} conversations)
                </span>
              </div>
              <p className="token-14 text-glass-secondary mt-2 font-mono bg-black/[0.03] px-2 py-1 rounded border border-black/[0.12] truncate">
                "{truncatedText}"
              </p>
              <p className="token-11 text-glass-muted mt-1">
                Yellow ring highlights the pattern message in each conversation
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap p-2 hover:bg-black/[0.05] rounded-lg transition-colors shrink-0 ml-4"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ds-amber" />
                <span className="ml-3 token-14 text-glass-muted">
                  Loading sample conversations...
                </span>
              </div>
            )}

            {error && (
              <div className="bg-ds-red/20 border border-ds-red/30 rounded-lg p-4 text-ds-red text-sm">
                Error loading samples: {error}
              </div>
            )}

            {!isLoading && !error && data.length === 0 && (
              <p className="text-center text-glass-muted py-12">
                No sample conversations found for this pattern.
              </p>
            )}

            {!isLoading && !error && data.length > 0 && (
              <ProofGridView
                conversations={conversations}
                organizationId={organizationId}
                timezone={timezone}
                onConversationClick={onConversationClick}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
